/**
 * Cards v2 — Zod schemas for the M2.1 extensions on chaos.cards
 * (spec §3.6) and chaos.card_members (spec §3.7).
 *
 * These extend (not replace) the legacy /api/kanban/cards/* contract.
 * Per-card field-PATCH endpoints under /api/cards/:cardId/* update
 * the new columns: assignee_*, role_id, card_instructions, workflow,
 * approval_policy. Card members are managed under
 * /api/cards/:cardId/members.
 */
import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────
// Assignee kind / member kind catalogs
// ─────────────────────────────────────────────────────────────────────
export const assigneeKinds = ['user', 'role', 'agent_instance'] as const;
export const assigneeKindSchema = z.enum(assigneeKinds);
export type AssigneeKind = z.infer<typeof assigneeKindSchema>;

export const memberKinds = ['user', 'role'] as const;
export const memberKindSchema = z.enum(memberKinds);
export type MemberKind = z.infer<typeof memberKindSchema>;

// ─────────────────────────────────────────────────────────────────────
// Per-card PATCH bodies — one Zod schema per dedicated endpoint.
// ─────────────────────────────────────────────────────────────────────

/**
 * PATCH /api/cards/:cardId/assignee — set/change/clear assignee.
 *
 * Hard constraint mirrors DB-level cards_single_assignee:
 *   - kind='user' → assigneeUserId set, assigneeRoleId null
 *   - kind='role' | 'agent_instance' → assigneeRoleId set, assigneeUserId null
 *   - kind=null (clear) → both must be null
 */
export const setCardAssigneeSchema = z
  .object({
    assigneeKind: assigneeKindSchema.nullable(),
    assigneeUserId: z.string().uuid().nullable().optional(),
    assigneeRoleId: z.string().uuid().nullable().optional(),
  })
  .superRefine((val, ctx) => {
    const userId = val.assigneeUserId ?? null;
    const roleId = val.assigneeRoleId ?? null;
    if (val.assigneeKind === 'user') {
      if (!userId) {
        ctx.addIssue({
          code: 'custom',
          message: 'assigneeUserId required when assigneeKind=user',
          path: ['assigneeUserId'],
        });
      }
      if (roleId) {
        ctx.addIssue({
          code: 'custom',
          message: 'assigneeRoleId must be null when assigneeKind=user',
          path: ['assigneeRoleId'],
        });
      }
    } else if (val.assigneeKind === 'role' || val.assigneeKind === 'agent_instance') {
      if (!roleId) {
        ctx.addIssue({
          code: 'custom',
          message: 'assigneeRoleId required when assigneeKind=role|agent_instance',
          path: ['assigneeRoleId'],
        });
      }
      if (userId) {
        ctx.addIssue({
          code: 'custom',
          message: 'assigneeUserId must be null when assigneeKind=role|agent_instance',
          path: ['assigneeUserId'],
        });
      }
    } else if (val.assigneeKind === null) {
      if (userId || roleId) {
        ctx.addIssue({
          code: 'custom',
          message: 'both ids must be null when clearing assignee',
          path: ['assigneeKind'],
        });
      }
    }
  });
export type SetCardAssigneeInput = z.infer<typeof setCardAssigneeSchema>;

/**
 * PATCH /api/cards/:cardId/responsible — change the card's ONE responsible
 * person (migration 0414). Never null: a card always has someone who answers
 * for it; to hand it over, name the new person.
 */
export const setCardResponsibleSchema = z.object({
  userId: z.string().uuid(),
});
export type SetCardResponsibleInput = z.infer<typeof setCardResponsibleSchema>;

/** GET /api/cards/:cardId/responsible, and the body of a successful PATCH. */
export const cardResponsibleSchema = z.object({
  cardId: z.string().uuid(),
  responsible: z.object({
    userId: z.string().uuid().nullable(),
    displayName: z.string().nullable(),
    /** 'set' = stored on the card; 'derived' = answered by the fallback chain. */
    source: z.enum(['set', 'derived']),
  }),
});
export type CardResponsible = z.infer<typeof cardResponsibleSchema>;

/** PATCH /api/cards/:cardId/role — set/clear the role authoring the card. */
export const setCardRoleSchema = z.object({
  roleId: z.string().uuid().nullable(),
});
export type SetCardRoleInput = z.infer<typeof setCardRoleSchema>;

/** PATCH /api/cards/:cardId/instructions — set/clear card-level instructions. */
export const setCardInstructionsSchema = z.object({
  cardInstructions: z.string().max(200_000).nullable(),
});
export type SetCardInstructionsInput = z.infer<typeof setCardInstructionsSchema>;

/** PATCH /api/cards/:cardId/workflow — set/clear workflow handoff pipeline. */
export const setCardWorkflowSchema = z.object({
  workflow: z.unknown().nullable(),
});
export type SetCardWorkflowInput = z.infer<typeof setCardWorkflowSchema>;

/** PATCH /api/cards/:cardId/approval-policy — set/clear policy string. */
export const setCardApprovalPolicySchema = z.object({
  approvalPolicy: z.string().max(200).nullable(),
});
export type SetCardApprovalPolicyInput = z.infer<typeof setCardApprovalPolicySchema>;

// ─────────────────────────────────────────────────────────────────────
// Card-member lifecycle
// ─────────────────────────────────────────────────────────────────────
export const cardMemberSchema = z.object({
  cardId: z.string().uuid(),
  memberKind: memberKindSchema,
  userId: z.string().uuid().nullable(),
  roleId: z.string().uuid().nullable(),
  addedByKind: memberKindSchema,
  addedByUserId: z.string().uuid().nullable(),
  addedByRoleId: z.string().uuid().nullable(),
  addedAt: z.string().datetime(),
});
export type CardMember = z.infer<typeof cardMemberSchema>;

/**
 * POST /api/cards/:cardId/members — add a member.
 *
 * Hard constraint mirrors DB-level card_members_payload_consistent:
 *   - memberKind='user' → userId set, roleId null
 *   - memberKind='role' → roleId set, userId null
 */
export const addCardMemberSchema = z
  .object({
    memberKind: memberKindSchema,
    userId: z.string().uuid().nullable().optional(),
    roleId: z.string().uuid().nullable().optional(),
  })
  .superRefine((val, ctx) => {
    const userId = val.userId ?? null;
    const roleId = val.roleId ?? null;
    if (val.memberKind === 'user') {
      if (!userId) {
        ctx.addIssue({
          code: 'custom',
          message: 'userId required when memberKind=user',
          path: ['userId'],
        });
      }
      if (roleId) {
        ctx.addIssue({
          code: 'custom',
          message: 'roleId must be null when memberKind=user',
          path: ['roleId'],
        });
      }
    } else if (val.memberKind === 'role') {
      if (!roleId) {
        ctx.addIssue({
          code: 'custom',
          message: 'roleId required when memberKind=role',
          path: ['roleId'],
        });
      }
      if (userId) {
        ctx.addIssue({
          code: 'custom',
          message: 'userId must be null when memberKind=role',
          path: ['userId'],
        });
      }
    }
  });
export type AddCardMemberInput = z.infer<typeof addCardMemberSchema>;
